/**
 * TON Connect integration layer.
 *
 * @tonconnect/ui-react — официальный SDK от TON Foundation, обязательный
 * для Telegram Mini App после Feb 2025 (Telegram требует TON Connect для
 * всех blockchain-операций).
 *
 * Этот файл — единственное место где импортируется @tonconnect/ui-react.
 * Остальной код проекта общается с wallet'ом через useTonWallet hook.
 */

import { CHAIN, SendTransactionRequest } from '@tonconnect/ui-react';
import { Address, Cell, beginCell } from '@ton/core';

/** URL манифеста — отдаётся статикой Vite из public/. */
export const TONCONNECT_MANIFEST_URL = (() => {
  const baseUrl = import.meta.env.VITE_PUBLIC_URL ?? window.location.origin;
  return `${baseUrl.replace(/\/$/, '')}/tonconnect-manifest.json`;
})();

/**
 * Конвертирует SignAndSendOpts (наш стабильный API из useTonWallet)
 * в SendTransactionRequest формат TON Connect.
 *
 * Особенности:
 *  - TON Connect не принимает Cell объекты напрямую — нужно base64
 *  - validUntil обязателен (без него wallet может зависнуть)
 *  - payload опционален: для simple TON transfer его нет
 *  - bounce wrapping: TonConnect использует raw адрес и отдельный флаг bounce
 *    через формат адреса (`EQ` vs `UQ`). См. конструкцию toString ниже.
 */
export function buildSendRequest(opts: {
  to: Address;
  value: bigint;
  body?: Cell;
  bounce?: boolean;
  network: 'testnet' | 'mainnet';
}): SendTransactionRequest {
  const validUntilSec = Math.floor(Date.now() / 1000) + 60; // +60 sec
  const bounce = opts.bounce ?? true;

  // TonConnect address format: UQ для non-bounceable, EQ/kQ/0Q вычисляются библиотекой
  // На самом деле для TonConnect message.address принимает raw (`0:abc...`) или friendly
  // и сама определяет bounce по контексту. Передаём friendly с правильным флагом.
  const friendlyAddr = opts.to.toString({
    bounceable: bounce,
    testOnly: opts.network === 'testnet',
    urlSafe: true,
  });

  return {
    validUntil: validUntilSec,
    network: opts.network === 'testnet' ? CHAIN.TESTNET : CHAIN.MAINNET,
    messages: [
      {
        address: friendlyAddr,
        amount: opts.value.toString(),
        payload: opts.body ? opts.body.toBoc().toString('base64') : undefined,
      },
    ],
  };
}

/** Простая helper для empty body (на случай если нужно явно). */
export function emptyBody(): Cell {
  return beginCell().endCell();
}
